import React, { useState, useEffect } from 'react';
import { TeamList } from './components/TeamList';
import { LoginPage } from './components/LoginPage';
import './App.css';
import { login, logout, checkAuth } from './api/apis';
import { StationsView } from './components/StationsView';
import { TeamManagement } from './components/TeamManagement';
import { StationsProvider } from './contexts/StationsContext';
import { HeatsView } from './components/HeatsView';
import RaceManagement from './components/RaceManagement/RaceManagement';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'results' | 'stations' | 'heats' | 'team-management' | 'race-management'>('results');

  const handleLogin = async (username: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const success = await login(username, password);
      if (success) {
        setIsAuthenticated(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsAuthenticated(false);
    setError(null);
  };

  const handleViewChange = (view: typeof activeView) => {
    setActiveView(view);
  };

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const isAuthed = await checkAuth();
        setIsAuthenticated(isAuthed);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Authentication check failed');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    verifyAuth();
  }, []);

  const renderContent = () => {
    if (!isAuthenticated) {
      return <LoginPage onLogin={handleLogin} error={error} />;
    }

    if (isLoading) {
      return (
        <div className="max-w-7xl mx-auto p-6 flex justify-center items-center min-h-screen">
          <div className="text-gray-600">Loading...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="max-w-7xl mx-auto p-6 flex justify-center items-center min-h-screen">
          <div className="text-red-600">{error}</div>
        </div>
      );
    }

    switch (activeView) {
      case 'results':
        return <TeamList />;
      case 'stations':
        return <StationsView />;
      case 'heats':
        return <HeatsView />;
      case 'team-management':
        return <TeamManagement />;
      case 'race-management':
        return <RaceManagement />;
      default:
        return <TeamManagement />;
    }
  };

  return (
    <div className="App">
      {isAuthenticated ? (
        <StationsProvider>
          <nav className="bg-gray-800 text-white p-4">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="flex space-x-8">
                <button
                  onClick={() => handleViewChange('team-management')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    activeView === 'team-management'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  Team Management
                </button>
                <button
                  onClick={() => handleViewChange('stations')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    activeView === 'stations'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  Stations Management
                </button>
                <button
                  onClick={() => handleViewChange('heats')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    activeView === 'heats'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  Heats Management
                </button>
                <button
                  onClick={() => handleViewChange('race-management')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    activeView === 'race-management'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  Race Management
                </button>
                <button
                  onClick={() => handleViewChange('results')}
                  className={`px-3 py-2 rounded-md text-sm font-medium ${
                    activeView === 'results'
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  Results
                </button>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              >
                Logout
              </button>
            </div>
          </nav>

          <div className="mt-6">
            {renderContent()}
          </div>
        </StationsProvider>
      ) : (
        renderContent()
      )}
    </div>
  );
}

export default App;